import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@osmosis/react-ui/components/Box';

import Divider from '../../Divider';
import { H4 } from '@osmosis/react-ui/components/Text';
import { H3 } from '@osmosis/react-ui/components/Text';
import { P } from '@osmosis/react-ui/components/Text';
import { faCheck } from '@fortawesome/pro-duotone-svg-icons/faCheck';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { Select } from '@osmosis/react-ui/components/Select';
import { Controller, useFormContext } from 'react-hook-form';
import { quizRepetitionSettingsValues } from '../../../constants';
import { bgGreen } from '@osmosis/tailwind-ui/styles/background';

const classMap = {
  positive: `${bgGreen}`,
  negative: 'bg-red-600',
};

/**
 * SelectHeader component with icon.
 */
export const SelectHeader = (props: {
  color: 'positive' | 'negative';
  text: string;
}) => {
  return (
    <Box>
      <Box p="5px 10px" color={props.color} alignItems="center" display="flex">
        <Icon style={{ marginRight: '10px' }} icon={faCheck} />
        <H3 color={props.color}>{props.text}</H3>
      </Box>
      <Divider cn={`mb-2.5 h-0.5 ${classMap[props.color]} `} />
    </Box>
  );
};

/**
 * Field for choosing repetitionsetting values.
 */
export const SelectField = ({
  label,
  name,
}: {
  label: string;
  name: string;
}) => {
  const colors = useColors();
  const { control } = useFormContext();
  const intl = useIntl();

  const ariaLabel = intl.formatMessage(
    {
      id: `repetitionSettingsFields.arialabel.${name}`,
      defaultMessage: `{preConfidenceText} Confidence`,
    },
    {
      preConfidenceText:
        name.charAt(0).toUpperCase() + // Capitalize first letter
        name.slice(1).replace('orrect', 'orrect ').replace('Med', 'Medium'), // Add space after 'correct' and unabbreviate 'Med'
    },
  );

  return (
    <Box mb={20} textAlign="center">
      <H4 color="gray60">{label}</H4>
      <P mb={10} color="gray30">
        <FormattedMessage
          id="repetitionSettingsFields.p"
          defaultMessage="confidence"
        />
      </P>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            bg={colors.gray5}
            style={{ minWidth: '120px', border: 'none' }}
            {...field}
            data-testid={`repetition-select-${name}`}
          >
            {quizRepetitionSettingsValues(intl).map(({ label, value }) => (
              <option
                key={value}
                value={value}
                data-testid={`repetition-option-${label}-${value}`}
                aria-label={ariaLabel}
              >
                {label}
              </option>
            ))}
          </Select>
        )}
      />
    </Box>
  );
};

/**
 * Component with correct and incorrect fields type.
 */
export const SelectFieldsBox = ({
  type,
}: {
  type: 'correct' | 'incorrect';
}) => {
  const intl = useIntl();
  return (
    <Box display="flex" justifyContent="space-around">
      <SelectField
        label={intl.formatMessage({
          id: 'repetitionSettingsFields.low',
          defaultMessage: 'Low',
        })}
        name={`${type}Low`}
      />

      <SelectField
        label={intl.formatMessage({
          id: 'common.medium',
          defaultMessage: 'Medium',
        })}
        name={`${type}Med`}
      />

      <SelectField
        label={intl.formatMessage({
          id: 'common.high',
          defaultMessage: 'High',
        })}
        name={`${type}High`}
      />
    </Box>
  );
};
